import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import {
  Save, Loader2, Eye, Code2, Info, FileText, ShieldCheck, FileSignature,
  ClipboardList, HeartHandshake, Plus, X, ExternalLink,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Legal documents CMS — tabbed split-pane HTML editor.
 * One tab per legal doc slug the tenant has. Each save auto-bumps `version`
 * and refreshes `body_sha256`. Every signed signup snapshots the exact hash
 * shown to the customer, so keeping this data honest is the whole ballgame.
 */

// Known slugs get a friendly icon + label + contextual info banner. Any
// additional slugs still surface as a tab (with a generic icon).
const KNOWN = {
  "nbn-terms": {
    icon: FileText,
    label: "NBN T&C",
    banner: (
      <>
        <strong className="text-white">Master NBN Terms &amp; Conditions.</strong>{" "}
        Referenced from the NBN CSA and included in every signed T&amp;C PDF. Public URL:{" "}
        <code className="text-white">stech.com.au/terms/nbn</code>.
      </>
    ),
  },
  privacy: {
    icon: ShieldCheck,
    label: "Privacy Policy",
    banner: (
      <>
        <strong className="text-white">Public privacy policy.</strong> Shown on the customer
        signup wizard and appended to every signed T&amp;C PDF. Public URL:{" "}
        <code className="text-white">stech.com.au/privacy</code>.
      </>
    ),
  },
  csa: {
    icon: FileSignature,
    label: "NBN CSA",
    banner: (
      <>
        <strong className="text-white">CSA placeholders</strong> are interpolated per-signup
        at accept time — leave these tokens as-is (preview shows raw tokens):{" "}
        <PlaceholderPills tokens={[
          "CUSTOMER_NAME","PLAN_NAME","MONTHLY_PRICE","DOWNLOAD_SPEED","UPLOAD_SPEED",
          "SPEED_TIER","TYPICAL_EVENING","CONNECTION_FEE","HARDWARE_DETAILS",
          "SERVICE_ADDRESS","CUSTOMER_EMAIL","CUSTOMER_PHONE","SIGNUP_REF",
        ]} />
      </>
    ),
  },
  cis: {
    icon: ClipboardList,
    label: "CIS Template",
    banner: (
      <>
        <strong className="text-white">Critical Information Summary template.</strong>{" "}
        ACMA requires a CIS to be readily available for every plan on sale. Placeholders
        interpolated at render time:{" "}
        <PlaceholderPills tokens={[
          "PLAN_NAME","MONTHLY_PRICE","DOWNLOAD_SPEED","UPLOAD_SPEED",
          "TYPICAL_EVENING","CONNECTION_FEE",
        ]} />
      </>
    ),
  },
  hardship: {
    icon: HeartHandshake,
    label: "Hardship",
    banner: (
      <>
        <strong className="text-white">Payment Assistance Policy.</strong> Required under
        the TCP Code C628:2019 — keep contact methods, response times, and TIO escalation
        paths accurate. Published at <code className="text-white">stech.com.au/hardship</code>.
      </>
    ),
  },
};

const ALLOWED_TAGS = "<h2>, <h3>, <p>, <strong>, <em>, <ol>, <ul>, <li>, <a href>, <table>, <tr>, <td>, <br>, <hr>";

function PlaceholderPills({ tokens }) {
  return (
    <span className="inline-flex flex-wrap gap-1 mt-1.5">
      {tokens.map((t) => (
        <code key={t} className="text-white bg-slate-800/80 border border-slate-700 rounded px-1.5 py-0.5 text-[10px] font-mono">
          {`{{${t}}}`}
        </code>
      ))}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminLegalDocs() {
  const [docs, setDocs] = useState(null);
  const [activeSlug, setActiveSlug] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("split"); // "split" | "source"
  const [newModal, setNewModal] = useState(false);

  async function reload(preserveActive = true) {
    try {
      const { data } = await api.get("/commissions/legal-docs");
      const list = data.docs || [];
      setDocs(list);
      if (list.length && (!preserveActive || !list.some((d) => d.slug === activeSlug))) {
        setActiveSlug(list[0].slug);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load legal docs");
      setDocs([]);
    }
  }
  useEffect(() => { reload(false); }, []);

  const current = useMemo(
    () => (docs || []).find((d) => d.slug === activeSlug),
    [docs, activeSlug],
  );

  // Refresh the draft whenever the active tab (or its underlying doc) changes
  // so unsaved edits on one tab don't leak into another.
  useEffect(() => {
    if (current) {
      setDraft({
        title: current.title || "",
        body_html: current.body_html || "",
        active: !!current.active,
        sort_order: current.sort_order ?? 0,
      });
    } else {
      setDraft(null);
    }
  }, [current?.slug, current?.version]);

  const dirty = !!(current && draft && (
    draft.title !== (current.title || "") ||
    draft.body_html !== (current.body_html || "") ||
    !!draft.active !== !!current.active ||
    Number(draft.sort_order) !== Number(current.sort_order ?? 0)
  ));

  async function save() {
    if (!current || !draft) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/commissions/legal-docs/${current.slug}`, {
        slug: current.slug,
        title: draft.title,
        body_html: draft.body_html,
        active: !!draft.active,
        sort_order: Number(draft.sort_order || 0),
      });
      toast.success(`Saved · v${data.version}`);
      // Re-fetch the whole list so the sidebar version pills refresh too
      await reload(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  }

  async function createDoc(payload) {
    try {
      await api.put(`/commissions/legal-docs/${payload.slug}`, payload);
      toast.success("Legal document created");
      setNewModal(false);
      setActiveSlug(payload.slug);
      await reload(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    }
  }

  return (
    <div className="space-y-5" data-testid="admin-legal-docs-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Legal documents</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Edit the T&amp;C, Privacy Policy, CSA, CIS and Hardship Policy shown on the public site,
            the signup wizard, and every signed PDF. Each save auto-bumps the version and refreshes
            the SHA-256 hash — every signup snapshots the exact hash the customer saw.
          </p>
        </div>
        <button
          data-testid="legal-doc-new"
          onClick={() => setNewModal(true)}
          className="shrink-0 inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <Plus className="w-4 h-4" /> New document
        </button>
      </div>

      {docs === null ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-8 flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading legal documents…
        </div>
      ) : docs.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-8 text-center text-sm text-slate-500">
          No legal documents yet. Hit <span className="text-slate-300">New document</span> to create the first one.
        </div>
      ) : (
        <>
          {/* Tab strip */}
          <div
            className="inline-flex flex-wrap gap-1 rounded-lg border border-slate-800 bg-slate-950/60 p-1"
            data-testid="legal-docs-tabs"
          >
            {docs.map((d) => {
              const meta = KNOWN[d.slug];
              const Icon = meta?.icon || FileText;
              const active = d.slug === activeSlug;
              return (
                <button
                  key={d.slug}
                  type="button"
                  onClick={() => setActiveSlug(d.slug)}
                  data-testid={`legal-tab-${d.slug}`}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold uppercase tracking-widest rounded-md transition-colors ${
                    active
                      ? "bg-cyan-500 text-slate-950"
                      : d.active
                        ? "text-slate-400 hover:text-white"
                        : "text-slate-500 hover:text-slate-300"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {meta?.label || d.slug}
                  {!d.active && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded ${active ? "bg-slate-950/25 text-slate-950" : "bg-slate-700/40 text-slate-400 border border-slate-600/40"}`}>
                      OFF
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {current && draft && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 space-y-4" data-testid="legal-editor-card">
              {/* Info banner */}
              <div className="flex items-start gap-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3 text-xs text-cyan-100/90">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-cyan-300" />
                <div className="space-y-1.5">
                  <p>
                    <span className="text-slate-300">Allowed HTML:</span>{" "}
                    <span className="font-mono text-white">{ALLOWED_TAGS}</span>. Anything else is stripped in the customer-facing render.
                  </p>
                  <p className="pt-1 border-t border-cyan-500/20">
                    {KNOWN[current.slug]?.banner || (
                      <><strong className="text-white">Custom legal document.</strong> Slug{" "}
                        <code className="text-white">{current.slug}</code>. Not part of the built-in
                        signup pipeline — used only where explicitly referenced.</>
                    )}
                  </p>
                </div>
              </div>

              {/* Title + slug + version */}
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_180px_140px] gap-4">
                <div>
                  <label className="text-slate-300 text-[10px] font-semibold uppercase tracking-widest mb-1.5 block">Title</label>
                  <input
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    data-testid="legal-title-input"
                    className="w-full rounded-lg border border-slate-700 bg-slate-950 text-white px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                  />
                </div>
                <div>
                  <label className="text-slate-300 text-[10px] font-semibold uppercase tracking-widest mb-1.5 block">Slug</label>
                  <input
                    value={current.slug}
                    disabled
                    className="w-full rounded-lg border border-slate-800 bg-slate-950/50 text-slate-500 font-mono text-sm px-3 py-2"
                  />
                </div>
                <div>
                  <label className="text-slate-300 text-[10px] font-semibold uppercase tracking-widest mb-1.5 block">Version</label>
                  <div className="rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2 font-mono text-sm text-cyan-300">
                    v{current.version}
                  </div>
                </div>
              </div>

              {/* Active + sort_order + preview toggle row */}
              <div className="flex flex-wrap items-center gap-6 justify-between">
                <div className="flex flex-wrap items-center gap-6">
                  <label className="inline-flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!draft.active}
                      onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                      data-testid="legal-active-toggle"
                      className="w-4 h-4 rounded border-slate-700 bg-slate-950 accent-cyan-500"
                    />
                    Active — include in signup snapshot
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Sort</span>
                    <input
                      type="number"
                      value={draft.sort_order}
                      onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })}
                      data-testid="legal-sort-input"
                      className="w-16 rounded border border-slate-700 bg-slate-950 text-white px-2 py-1 font-mono text-xs outline-none focus:border-cyan-500"
                    />
                  </label>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/public/legal-docs?slug=${current.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-300"
                  >
                    Public JSON <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="inline-flex rounded-md border border-slate-800 bg-slate-950/60 p-0.5">
                  <button
                    type="button"
                    onClick={() => setViewMode("split")}
                    data-testid="legal-view-split"
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest rounded ${
                      viewMode === "split" ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white"
                    }`}
                  >
                    <Eye className="w-3 h-3" /> Split
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("source")}
                    data-testid="legal-view-source"
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest rounded ${
                      viewMode === "source" ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white"
                    }`}
                  >
                    <Code2 className="w-3 h-3" /> Source only
                  </button>
                </div>
              </div>

              {/* Editor + preview */}
              <div className={`grid gap-4 ${viewMode === "split" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"}`}>
                <div>
                  <label className="text-slate-300 text-[10px] font-semibold uppercase tracking-widest mb-1.5 block">HTML source</label>
                  <textarea
                    value={draft.body_html}
                    onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
                    rows={26}
                    spellCheck={false}
                    data-testid="legal-html-textarea"
                    className="w-full rounded-lg border border-slate-700 bg-slate-950 text-slate-100 font-mono text-xs leading-relaxed px-3 py-2 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 resize-y"
                    placeholder="<h2>Section title</h2>&#10;<p>Body text …</p>"
                  />
                </div>
                {viewMode === "split" && (
                  <div>
                    <label className="text-slate-300 text-[10px] font-semibold uppercase tracking-widest mb-1.5 block">Live preview</label>
                    <div
                      className="rounded-lg border border-slate-800 bg-slate-950 p-5 max-h-[640px] overflow-y-auto text-sm text-slate-300 leading-relaxed
                                 [&_h2]:text-white [&_h2]:text-base [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:font-semibold
                                 [&_h3]:text-white [&_h3]:text-sm [&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:font-semibold
                                 [&_p]:my-2
                                 [&_strong]:text-white
                                 [&_a]:text-cyan-400 [&_a]:underline
                                 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2
                                 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2
                                 [&_li]:my-1
                                 [&_table]:w-full [&_table]:border-collapse [&_table]:my-3 [&_table]:text-xs
                                 [&_td]:border [&_td]:border-slate-800 [&_td]:px-3 [&_td]:py-1.5 [&_td]:align-top"
                      data-testid="legal-preview"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          draft.body_html || "<em class='text-slate-500'>Empty document.</em>",
                          // Legal docs need lists / tables / basic markup —
                          // but no script/iframe/on* handlers ever. Default
                          // profile handles that + strips unknown protocols.
                          { USE_PROFILES: { html: true } },
                        ),
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Save bar */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-800/70">
                <div className="text-xs text-slate-500 space-y-0.5">
                  <div className="font-mono">
                    sha-256 · <span className="text-slate-400">{(current.body_sha256 || "").slice(0, 24)}…</span>
                  </div>
                  <div>Updated {formatDate(current.updated_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  {dirty ? (
                    <span className="text-xs text-amber-300 font-semibold uppercase tracking-widest" data-testid="legal-dirty-indicator">
                      ● unsaved changes
                    </span>
                  ) : (
                    <span className="text-xs text-emerald-400 font-semibold uppercase tracking-widest">
                      ✓ all saved
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid="legal-save-button"
                    className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 px-5 py-2 rounded-lg text-sm font-semibold"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {newModal && (
        <NewDocModal onClose={() => setNewModal(false)} onCreate={createDoc} existingSlugs={(docs || []).map((d) => d.slug)} />
      )}
    </div>
  );
}

function NewDocModal({ onClose, onCreate, existingSlugs }) {
  const [form, setForm] = useState({ slug: "", title: "", active: true, sort_order: 0, body_html: "<h2>New section</h2>\n<p>Body…</p>" });
  const collision = existingSlugs.includes(form.slug);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()} data-testid="legal-new-modal">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display text-xl tracking-tight">New legal document</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); if (!collision && form.slug && form.title) onCreate(form); }}
          className="space-y-3"
        >
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Slug (URL key)</span>
            <input
              required autoFocus
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
              data-testid="legal-new-slug"
              placeholder="wisp-terms"
              className="w-full rounded-lg border border-slate-700 bg-slate-950 text-white font-mono px-3 py-2 outline-none focus:border-cyan-500"
            />
            {collision && <div className="text-xs text-red-400 mt-1">A doc with that slug already exists.</div>}
          </label>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Title</span>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="legal-new-title"
              placeholder="Fixed Wireless Terms & Conditions"
              className="w-full rounded-lg border border-slate-700 bg-slate-950 text-white px-3 py-2 outline-none focus:border-cyan-500"
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm text-slate-300">Cancel</button>
            <button
              type="submit"
              disabled={collision || !form.slug || !form.title}
              data-testid="legal-new-submit"
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-slate-950 px-4 py-2 rounded-lg text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
